import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Ticket, MessageSquare, CheckCircle2, AlertOctagon, Search, MessageCircle, Archive, ChevronRight,
} from "lucide-react";
import { adminApi } from "./api";
import { PageHeader, Pagination } from "./shared";
import { TicketDialog, ChatDialog } from "./SupportDialogs";
import { isSeen, markSeen, useSeenVersion } from "./seen";

const TONE = { ok: ["#16a34a", "#16a34a14"], warn: ["#f59e0b", "#f59e0b14"], danger: ["#e4002b", "#e4002b14"] };
const PRIORITY = { low: "#565b73", medium: "#f59e0b", high: "#e4002b", urgent: "#e4002b" };

const NewPill = () => (
  <span data-testid="new-pill" className="inline-flex items-center rounded-full bg-[#e4002b] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
    New
  </span>
);

const Avatar = ({ name }) => (
  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#2340b5]/10 text-xs font-bold text-[#2340b5]">
    {(name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
  </span>
);

const StatusPill = ({ label, tone }) => {
  const [c, bg] = TONE[tone] || TONE.warn;
  return (
    <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: c, backgroundColor: bg }}>
      {label}
    </span>
  );
};

export default function CustomerSupport() {
  const [tab, setTab] = useState("tickets");
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0, issue_types: [], stats: {} });
  const [issueType, setIssueType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [active, setActive] = useState(null);

  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  useSeenVersion(); // re-render when items are marked seen

  const openTicket = (t) => { markSeen("tickets", t.id); setActive(t); };
  const openChat = (c) => { markSeen("chats", c.id); setActiveChat(c); };

  // Deep link: /admin/support?ticket=<id> opens that ticket's dialog straight
  // from other sections (e.g. finance "View Dispute"). Falls back to a wide
  // fetch when the ticket isn't on the current page.
  useEffect(() => {
    const tid = searchParams.get("ticket");
    if (!tid || !data.items.length) return;
    const open = (t) => { if (t) { setTab("tickets"); openTicket(t); } setSearchParams({}, { replace: true }); };
    const match = data.items.find((t) => t.id === tid);
    if (match) { open(match); return; }
    adminApi.get("/support/cases", { params: { limit: 100 } })
      .then((r) => open((r.data.items || []).find((t) => t.id === tid)))
      .catch(() => open(null));
  }, [data, searchParams, setSearchParams]);

  const load = useCallback(() => {
    adminApi.get("/support/cases", { params: { issue_type: issueType, status, search, page } })
      .then((r) => setData(r.data));
  }, [issueType, status, search, page]);
  useEffect(() => { load(); }, [load]);

  const loadChats = useCallback(() => {
    adminApi.get("/support/chats").then((r) => setChats(r.data || []));
  }, []);
  useEffect(() => { if (tab === "chats") loadChats(); }, [tab, loadChats]);

  const stats = data.stats || {};
  const STAT = [
    { label: "Total Cases", value: stats.total, icon: Ticket, color: "#2340b5" },
    { label: "Open", value: stats.open, icon: MessageSquare, color: "#f59e0b" },
    { label: "Resolved", value: stats.resolved, icon: CheckCircle2, color: "#16a34a" },
    { label: "Urgent", value: stats.urgent, icon: AlertOctagon, color: "#e4002b" },
  ];

  const selCls = "rounded-xl border border-[#12142b]/12 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]";

  return (
    <div className="min-h-screen">
      <PageHeader title="Customer Support" subtitle="Chat with users about their tickets, disputes and ride conversations." />

      <div className="space-y-5 p-8">
        {/* Tabs */}
        <div className="flex w-fit rounded-xl border border-[#12142b]/12 bg-white p-1">
          <button data-testid="tab-tickets" onClick={() => setTab("tickets")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "tickets" ? "bg-[#2340b5] text-white" : "text-[#565b73]"}`}>
            <Ticket className="h-4 w-4" /> Tickets
          </button>
          <button data-testid="tab-chats" onClick={() => setTab("chats")}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "chats" ? "bg-[#2340b5] text-white" : "text-[#565b73]"}`}>
            <MessageCircle className="h-4 w-4" /> Ride Chats
          </button>
        </div>

        {tab === "tickets" && (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {STAT.map((s) => (
                <div key={s.label} data-testid={`support-stat-${s.label.toLowerCase().split(" ")[0]}`} className="rounded-2xl border border-[#12142b]/8 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium text-[#565b73]">{s.label}</p>
                    <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ backgroundColor: `${s.color}14` }}>
                      <s.icon className="h-4 w-4" strokeWidth={2} style={{ color: s.color }} />
                    </span>
                  </div>
                  <p className="mt-3 font-display text-2xl font-black text-[#12142b]">{(s.value ?? 0).toLocaleString()}</p>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#12142b]/8 bg-white p-4 shadow-sm">
              <div className="flex flex-1 items-center gap-2 rounded-xl border border-[#12142b]/12 px-3">
                <Search className="h-4 w-4 text-[#565b73]" />
                <input data-testid="support-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                  placeholder="Search subject, user or case ID…" className="w-full bg-transparent py-2.5 text-sm outline-none" />
              </div>
              <select data-testid="support-issue-filter" value={issueType} onChange={(e) => { setPage(1); setIssueType(e.target.value); }} className={selCls}>
                <option value="">All Issue Types</option>
                {data.issue_types?.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select data-testid="support-status-filter" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className={selCls}>
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            {/* Tickets table */}
            <div className="rounded-2xl border border-[#12142b]/8 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#12142b]/8 text-xs uppercase tracking-wide text-[#565b73]">
                      <th className="p-4 font-semibold">Case</th>
                      <th className="p-4 font-semibold">User</th>
                      <th className="p-4 font-semibold">Issue Type</th>
                      <th className="p-4 font-semibold">Status</th>
                      <th className="p-4 font-semibold">Priority</th>
                      <th className="p-4 font-semibold">Updated</th>
                      <th className="p-4 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((t) => {
                      const isNew = !isSeen("tickets", t.id);
                      return (
                      <tr key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => openTicket(t)}
                        className={`cursor-pointer border-b border-[#12142b]/5 last:border-0 hover:bg-[#f9fafd] ${isNew ? "bg-[#e4002b]/4" : ""}`}>
                        <td className={`p-4 ${isNew ? "border-l-2 border-[#e4002b]" : ""}`}>
                          <div className="flex items-center gap-2">
                            {isNew && <NewPill />}
                            <p className="font-semibold text-[#12142b]">{t.subject}</p>
                          </div>
                          <p className="text-xs text-[#565b73]">{t.case_id} · {t.last_message}</p>
                          {t.trip && (t.trip.route || t.trip.trip_code) && (
                            <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#2340b5]/8 px-2 py-0.5 text-[10px] font-semibold text-[#2340b5]" data-testid={`ticket-trip-${t.id}`}>
                              {t.trip.trip_code ? `${t.trip.trip_code} · ` : ""}{t.trip.route || "Linked trip"}{t.trip.departure ? ` · ${t.trip.departure}` : ""}
                            </p>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Avatar name={t.user_name} />
                            <div>
                              <p className="font-medium text-[#12142b]">{t.user_name}</p>
                              <p className="text-xs capitalize text-[#565b73]">{t.participant_role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-[#565b73]">{t.issue_type}</td>
                        <td className="p-4"><StatusPill label={t.status_label || t.status} tone={t.status_tone} /></td>
                        <td className="p-4 font-semibold capitalize" style={{ color: PRIORITY[t.priority] || "#565b73" }}>{t.priority}</td>
                        <td className="p-4 text-[#565b73]">{t.updated_pretty}</td>
                        <td className="p-4 text-right">
                          <button data-testid={`ticket-open-${t.id}`} onClick={(e) => { e.stopPropagation(); openTicket(t); }}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#2340b5]/8 px-3 py-1.5 text-xs font-semibold text-[#2340b5] hover:bg-[#2340b5]/15">
                            <MessageSquare className="h-3.5 w-3.5" /> {t.status === "resolved" ? "View" : "Reply"}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                    {data.items.length === 0 && (
                      <tr><td colSpan={7} className="py-12 text-center text-[#565b73]">No tickets match your filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="p-4"><Pagination page={data.page} pages={data.pages} onPage={setPage} /></div>
            </div>
          </>
        )}

        {tab === "chats" && (
          <div className="rounded-2xl border border-[#12142b]/8 bg-white p-2 shadow-sm">
            {chats.length === 0 && <p className="py-12 text-center text-sm text-[#565b73]">No ride conversations found.</p>}
            <div className="divide-y divide-[#12142b]/6">
              {chats.map((c) => {
                const isNew = c.unread_from_user && !isSeen("chats", c.id);
                return (
                <button key={c.id} data-testid={`chat-row-${c.id}`} onClick={() => openChat(c)}
                  className={`flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-[#f9fafd] ${isNew ? "bg-[#e4002b]/4" : ""}`}>
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#2340b5]/10">
                    <MessageCircle className="h-5 w-5 text-[#2340b5]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {isNew && <NewPill />}
                      <p className="truncate font-semibold text-[#12142b]">{c.route}</p>
                      {c.archived && (
                        <span className="flex items-center gap-1 rounded-full bg-[#565b73]/10 px-2 py-0.5 text-[10px] font-semibold text-[#565b73]">
                          <Archive className="h-3 w-3" /> Archived
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-[#565b73]">{c.rider_name} ↔ {c.driver_name} · {c.last_message}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-semibold text-[#12142b]">{c.message_count} msgs</p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-[#c3cbe4]" />
                </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <TicketDialog caseItem={active} onClose={() => setActive(null)} onChanged={load} />
      <ChatDialog chat={activeChat} onClose={() => setActiveChat(null)} />
    </div>
  );
}
